import React, { useEffect, useState } from 'react';
import { Box, keyframes, Typography } from '@mui/material';

// Glow pulse animation
const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
    transform: scale(0.95);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 193, 7, 0.8);
    transform: scale(1.05);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 193, 7, 0.5);
    transform: scale(0.95);
  }
`;

// Fade in and out
const fadeInOut = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

/**
 * NearMissGlow - Shows yellow glow with question mark when near miss
 * @param {Object} location - { lat, lng } location to show glow
 * @param {boolean} active - Whether glow is active
 * @param {Object} mapRef - Reference to map
 * @param {Function} onComplete - Callback when animation completes
 */
function NearMissGlow({ location, active, mapRef, onComplete }) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (active && location && mapRef) {
      const map = mapRef.getMap();
      if (!map) return;

      // Use Google Maps overlay to get screen coordinates
      const overlay = new window.google.maps.OverlayView();
      overlay.draw = function() {};
      overlay.onAdd = function() {
        const projection = this.getProjection();
        if (projection) {
          const latLng = new window.google.maps.LatLng(location.lat, location.lng);
          const point = projection.fromLatLngToContainerPixel(latLng);

          if (point) {
            setPosition({ x: point.x, y: point.y });
            setVisible(true);
          }
        }
      };
      overlay.onRemove = function() {};
      overlay.setMap(map);

      // Auto-hide after 0.8 seconds
      const timer = setTimeout(() => {
        setVisible(false);
        try {
          overlay.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
        if (onComplete) {
          onComplete();
        }
      }, 800);

      return () => {
        clearTimeout(timer);
        try {
          overlay.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
      };
    } else {
      setVisible(false);
    }
  }, [active, location, mapRef, onComplete]);

  if (!visible || !position) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 998,
      }}
    >
      {/* Yellow glow circle */}
      <Box
        sx={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: '80px',
          height: '80px',
          marginLeft: '-40px',
          marginTop: '-40px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 193, 7, 0.3)',
          border: '3px solid rgba(255, 193, 7, 0.8)',
          animation: `${glowPulse} 0.8s ease-in-out infinite, ${fadeInOut} 0.8s ease-in-out`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Question mark */}
        <Typography
          sx={{
            fontSize: '48px',
            fontWeight: 'bold',
            color: 'rgba(255, 193, 7, 1)',
            textShadow: '0 0 10px rgba(255, 193, 7, 0.8)',
          }}
        >
          ?
        </Typography>
      </Box>
    </Box>
  );
}

export default NearMissGlow;
